/** Minimal mdast node shape this transform reads and rewrites. */
interface MdNode {
  type: string;
  value?: string;
  children?: MdNode[];
}

/** Nodes whose text is literal and must keep its newlines as written. */
const LITERAL_NODE_TYPES: ReadonlySet<string> = new Set(["code", "inlineCode"]);

/**
 * remark plugin turning every single newline inside a paragraph into a hard
 * line break, the way a document author expects: an address block written on
 * five lines prints on five lines instead of reflowing into one. Equivalent to
 * `remark-breaks` without the dependency.
 * @returns The remark transformer
 */
export function remarkHardBreaks() {
  return (tree: MdNode): void => breakLines(tree);
}

/**
 * Recursively splits newline-bearing text nodes into text and break nodes.
 * @param node - The mdast node whose children are rewritten in place
 */
function breakLines(node: MdNode): void {
  if (!node.children || LITERAL_NODE_TYPES.has(node.type)) {
    return;
  }
  node.children = node.children.flatMap((child): MdNode[] => {
    if (child.type === "text" && typeof child.value === "string") {
      return splitOnNewlines(child.value);
    }
    breakLines(child);
    return [child];
  });
}

/**
 * Builds the replacement nodes for one text run, one `break` per newline.
 * @param text - The text run to split
 * @returns The text and break nodes, in order
 */
function splitOnNewlines(text: string): MdNode[] {
  const lines = text.split(/[\t ]*\r?\n[\t ]*/);
  return lines.flatMap((line, index) =>
    index === 0
      ? [{ type: "text", value: line }]
      : [{ type: "break" }, { type: "text", value: line }],
  );
}
